import React, { useState, useMemo, useEffect } from 'react';
import { useSkipsData } from '../context/SkipsDataContext';
import { useBatchedUpdates } from '../context/BatchedUpdatesContext';
import { WASTE_TYPES, SKIP_SIZES } from '../constants/jobOptions';
import { parseJobDate } from '../utils/dateUtils';

const RecordsTable = ({ jobs, onDelete, onEdit, onViewImage, onPrint, onViewOnMap, selectedRecords, onSelectionChange, highlightedRowId, focusJobId, onPaymentStatusChange, onStatusChange, onSizeChange, onWasteTypeChange, onDriverNoteChange, drivers, onAssignmentChange, userIsAdmin }) => {
    const { timeSettings } = useSkipsData();

    // Use batched updates from global context for optimistic UI updates
    const { queueUpdate, getCurrentValue } = useBatchedUpdates();
    const [editingPaymentJobId, setEditingPaymentJobId] = useState(null);
    const [editingStatusJobId, setEditingStatusJobId] = useState(null);
    const [editingSizeJobId, setEditingSizeJobId] = useState(null);
    const [editingWasteTypeJobId, setEditingWasteTypeJobId] = useState(null);
    const [editingAssignmentJobId, setEditingAssignmentJobId] = useState(null);
    const [editingDriverNoteJobId, setEditingDriverNoteJobId] = useState(null);
    const [driverNoteValue, setDriverNoteValue] = useState('');

    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'initialTime', direction: 'desc' });
    const [columnMenuOpen, setColumnMenuOpen] = useState(false);
    

    // Column visibility state with localStorage persistence
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem('columnVisibility');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) {
                console.error('Error parsing column visibility from localStorage:', e);
            }
        }
        // Default: all columns visible except Skip ID and Note
        return {
            skipId: false,
            address: true,
            daysOnHire: true,
            dateTime: true,
            size: true,
            wasteType: true,
            paymentStatus: true,
            status: true,
            assignedTo: true,
            driverNote: false, // Hidden by default
            actions: true // Always true
        };
    });

    // Save to localStorage whenever visibility changes
    useEffect(() => {
        localStorage.setItem('columnVisibility', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    // Toggle column visibility
    const toggleColumn = (columnKey) => {
        if (columnKey === 'actions') return; // Actions column always visible
        setVisibleColumns(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
        }));
    };

    // Reset all columns to default (Skip ID and Note hidden)
    const resetColumns = () => {
        const defaultVisible = {
            skipId: false,
            address: true,
            daysOnHire: true,
            dateTime: true,
            size: true,
            wasteType: true,
            paymentStatus: true,
            status: true,
            assignedTo: true,
            driverNote: false,
            actions: true
        };
        setVisibleColumns(defaultVisible);
    };

    // Use internal state if parent doesn't provide selection management
    const [internalSelectedRecords, setInternalSelectedRecords] = useState(new Set());
    const actualSelectedRecords = selectedRecords !== undefined ? selectedRecords : internalSelectedRecords;
    const setActualSelectedRecords = onSelectionChange || setInternalSelectedRecords;

    const recordsPerPage = 25;

    // Payment status badge styles (semantic colours from design system)
    const getPaymentBadgeStyle = (paymentStatus) => {
        const styles = {
            'Paid': { background: '#D1FAE5', color: '#065F46' },           // Success
            'TO PAY': { background: '#FEF3C7', color: '#92400E' },         // Attention
            'Account': { background: '#F1F5F9', color: '#475569' },        // Neutral
            'Unknown': { background: '#F5F5F4', color: '#78716C' }         // Neutral grey
        };
        return styles[paymentStatus] || styles['Unknown'];
    };

    // Status badge styles
    const getStatusBadgeStyle = (status) => {
        const styles = {
            'delivered': { background: '#F1F5F9', color: '#475569' },      // Neutral
            'collected': { background: '#D1FAE5', color: '#065F46' }       // Success
        };
        return styles[status?.toLowerCase()] || { background: '#F5F5F4', color: '#78716C' };
    };

    // Custom sort for skip sizes - moved outside useMemo to fix ESLint warning
    const sizeOrder = useMemo(() =>
        ['2yd', '4yd', '6yd', '8yd', '8yd lock', '10yd', '12yd', '12yd lock', '14yd', '16yd', '16yd lock', '20yd', '40yd'],
        []
    );

    // Sort jobs
    const sortedJobs = useMemo(() => {
        let sortableJobs = [...jobs];

        if (sortConfig.key) {
            sortableJobs.sort((a, b) => {
                let aValue, bValue;

                switch (sortConfig.key) {
                    case 'initialTime':
                        aValue = (parseJobDate(a.initialTime, a.timestamp) || new Date(0)).getTime();
                        bValue = (parseJobDate(b.initialTime, b.timestamp) || new Date(0)).getTime();
                        break;
                    case 'address':
                        aValue = (a.formattedAddress || a.streetAddress || '').toLowerCase();
                        bValue = (b.formattedAddress || b.streetAddress || '').toLowerCase();
                        break;
                    case 'daysOnHire': {
                        const aDate = parseJobDate(a.initialTime, a.timestamp);
                        const bDate = parseJobDate(b.initialTime, b.timestamp);
                        aValue = aDate ? Math.round((Date.now() - aDate) / (1000 * 60 * 60 * 24)) : 0;
                        bValue = bDate ? Math.round((Date.now() - bDate) / (1000 * 60 * 60 * 24)) : 0;
                        break;
                    }
                    case 'size':
                        aValue = sizeOrder.indexOf(a.size) !== -1 ? sizeOrder.indexOf(a.size) : 999;
                        bValue = sizeOrder.indexOf(b.size) !== -1 ? sizeOrder.indexOf(b.size) : 999;
                        break;
                    case 'wasteType':
                        aValue = (a.wasteType || a.description || '').toLowerCase();
                        bValue = (b.wasteType || b.description || '').toLowerCase();
                        break;
                    case 'status':
                        aValue = (a.status || '').toLowerCase();
                        bValue = (b.status || '').toLowerCase();
                        break;
                    case 'assignedTo':
                        aValue = (a.assignedDriverName || '').toLowerCase();
                        bValue = (b.assignedDriverName || '').toLowerCase();
                        break;
                    default:
                        aValue = a[sortConfig.key];
                        bValue = b[sortConfig.key];
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return sortableJobs;
    }, [jobs, sortConfig, sizeOrder]);

    // Navigate to page containing focusJobId
    useEffect(() => {
        if (focusJobId && sortedJobs.length > 0) {
            const jobIndex = sortedJobs.findIndex(job => job.id === focusJobId);
            if (jobIndex !== -1) {
                const targetPage = Math.floor(jobIndex / recordsPerPage) + 1;
                if (targetPage !== currentPage) {
                    setCurrentPage(targetPage);
                }
            }
        }
    }, [focusJobId, sortedJobs, currentPage, recordsPerPage]);

    // Pagination
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = sortedJobs.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(sortedJobs.length / recordsPerPage);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prevConfig => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Handle select all
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(currentRecords.map(job => job.id));
            setActualSelectedRecords(allIds);
        } else {
            setActualSelectedRecords(new Set());
        }
    };

    // Handle individual selection
    const handleSelectRecord = (id) => {
        setActualSelectedRecords(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    const handleWasteTypeChange = (jobId, newWasteType) => {
        // Queue the update for batching (optimistic update)
        queueUpdate(jobId, 'wasteType', newWasteType);
        setEditingWasteTypeJobId(null);
    };

    const handleSizeChange = (jobId, newSize) => {
        // Queue the update for batching (optimistic update)
        queueUpdate(jobId, 'size', newSize);
        setEditingSizeJobId(null);
    };

    const handleStatusChange = (jobId, newStatus) => {
        // Queue the update for batching (optimistic update)
        queueUpdate(jobId, 'status', newStatus);
        setEditingStatusJobId(null);
    };

    const handlePaymentChange = (jobId, newStatus) => {
        // Queue the update for batching (optimistic update)
        queueUpdate(jobId, 'paymentStatus', newStatus);
        setEditingPaymentJobId(null);
    };

    const handleInvoicedToggle = (jobId, currentValue) => {
        if (!userIsAdmin) return;
        queueUpdate(jobId, 'invoiced', !currentValue);
    };

    const handleDriverNoteChange = (jobId) => {
        // Queue the update for batching (optimistic update)
        queueUpdate(jobId, 'driverNote', driverNoteValue);
        setEditingDriverNoteJobId(null);
        setDriverNoteValue('');
    };

    // Copy to clipboard
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Get sort indicator
    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return ' ↕';
        return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
    };

    if (jobs.length === 0) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '400px',
                color: '#6B7280'
            }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                <div style={{ fontSize: '20px', fontWeight: '500' }}>No Records Found</div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                    Try adjusting your filters or add new skip records
                </div>
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            {/* Column visibility control */}
            <div style={{
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'flex-end',
                position: 'relative'
            }}>
                <button
                    onClick={() => setColumnMenuOpen(!columnMenuOpen)}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: 'white',
                        border: '1px solid #D1D5DB',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    ⚙️ Columns
                </button>

                {columnMenuOpen && (
                    <>
                        {/* Backdrop to close menu */}
                        <div
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 999
                            }}
                            onClick={() => setColumnMenuOpen(false)}
                        />
                        {/* Dropdown menu */}
                        <div style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: '4px',
                            backgroundColor: 'white',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            padding: '8px',
                            minWidth: '200px',
                            zIndex: 1000
                        }}>
                            <div style={{
                                padding: '8px 12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: '#6B7280',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                borderBottom: '1px solid #E5E7EB',
                                marginBottom: '8px'
                            }}>
                                Toggle Columns
                            </div>

                            {[
                                { key: 'address', label: 'Address' },
                                { key: 'daysOnHire', label: 'Hire Days' },
                                { key: 'paymentStatus', label: 'Payment' },
                                { key: 'status', label: 'Status' },
                                { key: 'assignedTo', label: 'Assigned To' },
                                { key: 'size', label: 'Size' },
                                { key: 'wasteType', label: 'Waste Type' },
                                { key: 'dateTime', label: 'Date/Time' },
                                { key: 'driverNote', label: 'Note' },
                                { key: 'skipId', label: 'Skip ID' },
                                { key: 'actions', label: 'Actions', disabled: true }
                            ].map(({ key, label, disabled }) => (
                                <label
                                    key={key}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px 12px',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        borderRadius: '4px',
                                        fontSize: '14px',
                                        color: disabled ? '#9CA3AF' : '#374151',
                                        backgroundColor: 'transparent',
                                        transition: 'background-color 0.15s'
                                    }}
                                    onMouseOver={(e) => !disabled && (e.currentTarget.style.backgroundColor = '#F3F4F6')}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    <input
                                        type="checkbox"
                                        checked={visibleColumns[key]}
                                        onChange={() => toggleColumn(key)}
                                        disabled={disabled}
                                        style={{
                                            marginRight: '10px',
                                            cursor: disabled ? 'not-allowed' : 'pointer'
                                        }}
                                    />
                                    {label}
                                </label>
                            ))}

                            <div style={{
                                borderTop: '1px solid #E5E7EB',
                                marginTop: '8px',
                                paddingTop: '8px'
                            }}>
                                <button
                                    onClick={resetColumns}
                                    style={{
                                        width: '100%',
                                        padding: '8px 12px',
                                        backgroundColor: '#F3F4F6',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        color: '#374151'
                                    }}
                                    onMouseOver={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                                    onMouseOut={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                                >
                                    Reset to Default
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Selection info */}
            {actualSelectedRecords.size > 0 && (
                <div style={{
                    padding: '12px 16px',
                    backgroundColor: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{ color: '#1E40AF', fontWeight: '500' }}>
                        {actualSelectedRecords.size} record{actualSelectedRecords.size !== 1 ? 's' : ''} selected
                    </span>
                    <button
                        onClick={() => setActualSelectedRecords(new Set())}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: 'white',
                            border: '1px solid #BFDBFE',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#1E40AF'
                        }}
                    >
                        Clear Selection
                    </button>
                </div>
            )}

            {/* Table */}
            <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: 'white'
                }}>
                    <thead>
                        <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '2px solid #E5E7EB' }}>
                            <th style={{ padding: '12px', textAlign: 'left', width: '40px' }}>
                                <input
                                    type="checkbox"
                                    checked={actualSelectedRecords.size === currentRecords.length && currentRecords.length > 0}
                                    onChange={handleSelectAll}
                                    style={{ cursor: 'pointer' }}
                                />
                            </th>
                            {visibleColumns.address && (
                                <th
                                    onClick={() => handleSort('address')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151'
                                    }}
                                >
                                    Address{getSortIndicator('address')}
                                </th>
                            )}
                            {visibleColumns.daysOnHire && (
                                <th
                                    onClick={() => handleSort('daysOnHire')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151'
                                    }}
                                >
                                    Hire Days{getSortIndicator('daysOnHire')}
                                </th>
                            )}
                            {visibleColumns.paymentStatus && (
                                <th
                                    onClick={() => handleSort('paymentStatus')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151',
                                        minWidth: '120px'
                                    }}
                                >
                                    Payment{getSortIndicator('paymentStatus')}
                                </th>
                            )}
                            {visibleColumns.status && (
                                <th
                                    onClick={() => handleSort('status')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151',
                                        minWidth: '120px'
                                    }}
                                >
                                    Status{getSortIndicator('status')}
                                </th>
                            )}
                            {visibleColumns.assignedTo && (
                                <th
                                    onClick={() => handleSort('assignedTo')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151',
                                        minWidth: '140px'
                                    }}
                                >
                                    Assigned To{getSortIndicator('assignedTo')}
                                </th>
                            )}
                            {visibleColumns.size && (
                                <th
                                    onClick={() => handleSort('size')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151',
                                        minWidth: '120px'
                                    }}
                                >
                                    Size{getSortIndicator('size')}
                                </th>
                            )}
                            {visibleColumns.wasteType && (
                                <th
                                    onClick={() => handleSort('wasteType')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151',
                                        minWidth: '120px'
                                    }}
                                >
                                    Waste Type{getSortIndicator('wasteType')}
                                </th>
                            )}
                            {visibleColumns.dateTime && (
                                <th
                                    onClick={() => handleSort('initialTime')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151'
                                    }}
                                >
                                    Date/Time{getSortIndicator('initialTime')}
                                </th>
                            )}
                            {visibleColumns.skipId && (
                                <th
                                    onClick={() => handleSort('skipId')}
                                    style={{
                                        padding: '12px',
                                        textAlign: 'left',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '14px',
                                        color: '#374151'
                                    }}
                                >
                                    Skip ID{getSortIndicator('skipId')}
                                </th>
                            )}
                            {visibleColumns.driverNote && (
                                <th style={{
                                    padding: '12px',
                                    textAlign: 'left',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    color: '#374151',
                                    minWidth: '200px'
                                }}>
                                    Note
                                </th>
                            )}
                            {visibleColumns.actions && (
                                <th style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    fontWeight: '600',
                                    fontSize: '14px',
                                    color: '#374151',
                                    width: '80px'
                                }}>
                                    Actions
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {currentRecords.map((job, index) => (
                            <tr
                                key={job.id}
                                data-job-id={job.id}
                                style={{
                                    borderBottom: '1px solid #E5E7EB',
                                    backgroundColor: highlightedRowId === job.id
                                        ? '#FEF3C7'
                                        : (index % 2 === 0 ? 'white' : '#F9FAFB'),
                                    transition: 'background-color 3s ease-out'
                                }}
                                onMouseOver={(e) => {
                                    if (highlightedRowId !== job.id) {
                                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if (highlightedRowId !== job.id) {
                                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#F9FAFB';
                                    } else {
                                        e.currentTarget.style.backgroundColor = '#FEF3C7';
                                    }
                                }}
                            >
                                <td style={{ padding: '12px' }}>
                                    <input
                                        type="checkbox"
                                        checked={actualSelectedRecords.has(job.id)}
                                        onChange={() => handleSelectRecord(job.id)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </td>
                                {visibleColumns.address && (
                                    <td style={{ padding: '12px', fontSize: '14px', maxWidth: '400px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {job.formattedAddress || job.streetAddress || 'N/A'}
                                    </td>
                                )}
                                {visibleColumns.daysOnHire && (
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                        {(() => {
                                            const jobDate = parseJobDate(job.initialTime, job.timestamp);
                                            if (!jobDate) return '0';
                                            const daysOnHire = Math.round((Date.now() - jobDate) / (1000 * 60 * 60 * 24));

                                            // Check if overdue (red threshold)
                                            if (daysOnHire > timeSettings.yellow) {
                                                const daysOverdue = daysOnHire - timeSettings.yellow;
                                                return (
                                                    <span>
                                                        {daysOnHire} <span style={{ color: '#EF4444', fontWeight: '500', fontSize: '0.85em' }}>+{daysOverdue}</span>
                                                    </span>
                                                );
                                            }
                                            return daysOnHire;
                                        })()}
                                    </td>
                                )}
                                {visibleColumns.paymentStatus && (
                                    <td style={{ padding: '12px', cursor: 'pointer' }} onClick={() => setEditingPaymentJobId(job.id)} >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {editingPaymentJobId === job.id ? (
                                            <select
                                                defaultValue={getCurrentValue(job.id, 'paymentStatus', job.paymentStatus || 'Unknown')}
                                                onChange={(e) => handlePaymentChange(job.id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                <option value="TO PAY">TO PAY</option>
                                                <option value="Paid">Paid</option>
                                                <option value="Account">Account</option>
                                                <option value="Unknown">Unknown</option>
                                            </select>
                                        ) : (() => {
                                            const badgeStyle = getPaymentBadgeStyle(getCurrentValue(job.id, 'paymentStatus', job.paymentStatus || 'Unknown'));
                                            return (
                                            <span style={{
                                                padding: '4px 8px',
                                                backgroundColor: badgeStyle.background,
                                                color: badgeStyle.color,
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.025em'
                                            }}>
                                                {getCurrentValue(job.id, 'paymentStatus', job.paymentStatus || 'Unknown')}
                                            </span>
                                            );
                                        })()}
                                        {/* Invoiced badge */}
                                        {(() => {
                                            const isInvoiced = getCurrentValue(job.id, 'invoiced', job.invoiced === true);
                                            return (
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleInvoicedToggle(job.id, isInvoiced);
                                                    }}
                                                    style={{
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        fontSize: '10px',
                                                        fontWeight: '600',
                                                        cursor: userIsAdmin ? 'pointer' : 'default',
                                                        userSelect: 'none',
                                                        ...(isInvoiced
                                                            ? { backgroundColor: '#DBEAFE', color: '#1E40AF' }
                                                            : { backgroundColor: 'transparent', color: '#9CA3AF', border: '1px dashed #D1D5DB' }
                                                        )
                                                    }}
                                                    title={userIsAdmin ? (isInvoiced ? 'Click to mark as not invoiced' : 'Click to mark as invoiced') : (isInvoiced ? 'Invoiced' : 'Not invoiced')}
                                                >
                                                    {isInvoiced ? 'INV' : 'No inv'}
                                                </span>
                                            );
                                        })()}
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.status && (
                                    <td style={{ padding: '12px', cursor: 'pointer' }} onClick={() => setEditingStatusJobId(job.id)}>
                                        {editingStatusJobId === job.id ? (
                                            <select
                                                defaultValue={getCurrentValue(job.id, 'status', job.status || 'N/A')}
                                                onChange={(e) => handleStatusChange(job.id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                <option value="delivered">Delivered</option>
                                                <option value="collected">Collected</option>
                                            </select>
                                        ) : (() => {
                                            const badgeStyle = getStatusBadgeStyle(getCurrentValue(job.id, 'status', job.status));
                                            return (
                                            <span style={{
                                                padding: '4px 8px',
                                                backgroundColor: badgeStyle.background,
                                                color: badgeStyle.color,
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: '500',
                                                textTransform: 'capitalize'
                                            }}>
                                                {getCurrentValue(job.id, 'status', job.status || 'N/A')}
                                            </span>
                                            );
                                        })()}
                                    </td>
                                )}
                                {visibleColumns.assignedTo && (
                                    <td style={{ padding: '12px', cursor: userIsAdmin ? 'pointer' : 'default' }} onClick={() => userIsAdmin && setEditingAssignmentJobId(job.id)}>
                                        {editingAssignmentJobId === job.id && userIsAdmin ? (
                                            <select
                                                defaultValue={job.assignedDriverId || ''}
                                                onChange={(e) => {
                                                    const selectedDriver = drivers.find(d => d.id === e.target.value);
                                                    onAssignmentChange(job.id, e.target.value || null, selectedDriver?.name || null);
                                                    setEditingAssignmentJobId(null);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                onBlur={() => setEditingAssignmentJobId(null)}
                                                autoFocus
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                    minWidth: '120px'
                                                }}
                                            >
                                                <option value="">None</option>
                                                {drivers.map((driver) => (
                                                    <option key={driver.id} value={driver.id}>
                                                        {driver.name}{driver.isAdmin ? ' (Admin)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span style={{
                                                padding: '4px 8px',
                                                backgroundColor: job.assignedDriverId ? '#E0E7FF' : '#F3F4F6',
                                                color: job.assignedDriverId ? '#3730A3' : '#6B7280',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}>
                                                {job.assignedDriverName || 'None'}
                                            </span>
                                        )}
                                    </td>
                                )}
                                {visibleColumns.size && (
                                    <td style={{ padding: '12px', cursor: 'pointer' }} onClick={() => setEditingSizeJobId(job.id)}>
                                        {editingSizeJobId === job.id ? (
                                            <select
                                                defaultValue={getCurrentValue(job.id, 'size', job.size || 'N/A')}
                                                onChange={(e) => handleSizeChange(job.id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                {SKIP_SIZES.map((size) => (
                                                    <option key={size} value={size}>{size}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span style={{
                                                fontSize: '14px',
                                                color: '#57534E'
                                            }}>
                                                {getCurrentValue(job.id, 'size', job.size || 'N/A')}
                                            </span>
                                        )}
                                    </td>
                                )}
                                {visibleColumns.wasteType && (
                                    <td style={{ padding: '12px', cursor: 'pointer' }} onClick={() => setEditingWasteTypeJobId(job.id)}>
                                        {editingWasteTypeJobId === job.id ? (
                                            <select
                                                defaultValue={getCurrentValue(job.id, 'wasteType', job.wasteType || job.description || 'N/A')}
                                                onChange={(e) => handleWasteTypeChange(job.id, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                                autoFocus
                                                style={{
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ccc',
                                                    fontSize: '12px',
                                                    fontWeight: '500',
                                                }}
                                            >
                                                {WASTE_TYPES.map((wasteType) => (
                                                    <option key={wasteType} value={wasteType}>{wasteType}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <span style={{
                                                fontSize: '14px',
                                                color: '#57534E'
                                            }}>
                                                {getCurrentValue(job.id, 'wasteType', job.wasteType || job.description || 'N/A')}
                                            </span>
                                        )}
                                    </td>
                                )}
                                {visibleColumns.dateTime && (
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                        {(() => {
                                            const d = parseJobDate(job.initialTime, job.timestamp);
                                            if (!d) return 'N/A';
                                            return (
                                                <>
                                                    <div>{d.toLocaleDateString('en-GB')}</div>
                                                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                                        {d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </td>
                                )}
                                {visibleColumns.skipId && (
                                    <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'monospace' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span>{(job.skipId || '').substring(0, 8)}...</span>
                                            <button
                                                onClick={() => copyToClipboard(job.skipId || '')}
                                                title="Copy to clipboard"
                                                style={{
                                                    padding: '4px 8px',
                                                    backgroundColor: '#F3F4F6',
                                                    border: '1px solid #D1D5DB',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '11px'
                                                }}
                                            >
                                                📋
                                            </button>
                                        </div>
                                    </td>
                                )}
                                {visibleColumns.driverNote && (
                                    <td style={{ padding: '12px', fontSize: '14px', maxWidth: '300px' }}>
                                        {editingDriverNoteJobId === job.id && userIsAdmin ? (
                                            <div style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                                                <textarea
                                                    value={driverNoteValue}
                                                    onChange={(e) => setDriverNoteValue(e.target.value)}
                                                    placeholder="Enter driver note..."
                                                    maxLength={250}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px',
                                                        fontSize: '14px',
                                                        border: '1px solid #3B82F6',
                                                        borderRadius: '4px',
                                                        outline: 'none',
                                                        resize: 'vertical',
                                                        minHeight: '60px',
                                                        fontFamily: 'inherit'
                                                    }}
                                                    autoFocus
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Escape') {
                                                            setEditingDriverNoteJobId(null);
                                                            setDriverNoteValue('');
                                                        }
                                                    }}
                                                />
                                                <button
                                                    onClick={() => handleDriverNoteChange(job.id)}
                                                    style={{
                                                        padding: '8px 12px',
                                                        backgroundColor: '#10B981',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingDriverNoteJobId(null);
                                                        setDriverNoteValue('');
                                                    }}
                                                    style={{
                                                        padding: '8px 12px',
                                                        backgroundColor: '#EF4444',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ) : (
                                            <div
                                                onClick={() => {
                                                    if (userIsAdmin) {
                                                        setEditingDriverNoteJobId(job.id);
                                                        const currentNote = getCurrentValue(job.id, 'driverNote', job.driverNote || '');
                                                        setDriverNoteValue(currentNote);
                                                    }
                                                }}
                                                style={{
                                                    cursor: userIsAdmin ? 'pointer' : 'default',
                                                    padding: '6px',
                                                    borderRadius: '4px',
                                                    backgroundColor: userIsAdmin ? 'transparent' : 'inherit',
                                                    transition: 'background-color 0.15s',
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    color: getCurrentValue(job.id, 'driverNote', job.driverNote) ? '#374151' : '#9CA3AF',
                                                    fontStyle: getCurrentValue(job.id, 'driverNote', job.driverNote) ? 'normal' : 'italic',
                                                    fontSize: '13px'
                                                }}
                                                onMouseOver={(e) => userIsAdmin && (e.currentTarget.style.backgroundColor = '#F3F4F6')}
                                                onMouseOut={(e) => userIsAdmin && (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                {getCurrentValue(job.id, 'driverNote', job.driverNote) || (userIsAdmin ? 'Click to add note...' : 'No note')}
                                            </div>
                                        )}
                                    </td>
                                )}
                                {visibleColumns.actions && (
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                            <button
                                                onClick={() => onViewOnMap(job)}
                                                title="View on Map"
                                                style={{
                                                    padding: '6px 10px',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px'
                                                }}
                                            >
                                                🗺️
                                            </button>
                                            <button
                                                onClick={() => onEdit(job)}
                                                title="Edit Job"
                                                style={{
                                                    padding: '6px 10px',
                                                    backgroundColor: 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '18px'
                                                }}
                                            >
                                                ✏️
                                            </button>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div style={{
                marginTop: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ fontSize: '14px', color: '#6B7280' }}>
                    Showing {indexOfFirstRecord + 1} to {Math.min(indexOfLastRecord, sortedJobs.length)} of {sortedJobs.length} records
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: currentPage === 1 ? '#F3F4F6' : '#3B82F6',
                            color: currentPage === 1 ? '#9CA3AF' : 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Previous
                    </button>
                    <span style={{ padding: '8px 16px', fontSize: '14px', color: '#374151' }}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: currentPage === totalPages ? '#F3F4F6' : '#3B82F6',
                            color: currentPage === totalPages ? '#9CA3AF' : 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                        }}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecordsTable;
